import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import WelcomeScreenCard from './welcome_screen_card';
import { gaps } from 'components/layout/stack';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${gaps['2xl']};
  justify-content: center;
  background: white;
  border-radius: 10px;
  overflow: ${props => props.hasBanner ? 'hidden' : 'visible'};

  padding: 0 0 ${gaps.xl} 0;

  @media (max-width: 960px) {
    border-radius: 0;
    gap: ${gaps.lg};
  }
`;

const BannerImage = styled.img`
  width: 100%;
  margin: 0 0 -${gaps.md} 0;
  display: block;
  object-fit: cover;
  max-height: 300px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.sm};
  justify-content: center;

  padding: ${props => props.hasBanner ? gaps.md : gaps['2xl']} 0 0 0;

  @media (max-width: 960px) {
    padding: ${props => props.hasBanner ? gaps.md : gaps.xl} ${gaps.xl} 0 ${gaps.xl};
  }

  & > * {
    margin: 0;
  }

  @media (min-width: 551px) {
    align-items: center;
  }
`;

const Title = styled.h1`
  font-size: 40px;
  line-height: normal;

  ${props => props.hasBanner && `
    @media (max-width: 960px) {
      font-size: 32px;
    }
  `}
`;

const Subtitle = styled.div`
  font-size: 16px;
`;

const listWrapperStyles = `
  flex-direction: column;
  gap: ${gaps.sm};
  padding: 0 ${gaps.md};
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${gaps.md};
  justify-content: center;
  // 1-3, 5-6 all look good with 1000px, only exception for 4 cards to be 2x2
  max-width: ${props => props.pathCount === 4 ? '800px' : '1000px'};

  ${props => props.viewType === 'list' ? listWrapperStyles : `
    @media (max-width: 960px) {
      ${listWrapperStyles}
    }
  `}
`;

const WelcomeScreen = observer(({ learningPaths, signedIn, onLearningPathClick, viewType }) => {
  if (!learningPaths || learningPaths.length === 0) {
    return null;
  }

  const title = signedIn
    ? AppUI.site.learningPathsWelcomeTitleSignedIn
    : AppUI.site.learningPathsWelcomeTitleSignedOut;

  const subtitle = signedIn
    ? AppUI.site.learningPathsWelcomeSubtitleSignedIn
    : AppUI.site.learningPathsWelcomeSubtitleSignedOut;

  return (
    <Wrapper hasBanner={isPresent(AppUI.site.heroBanner?.image?.url)}>
      {
        isPresent(AppUI.site.heroBanner?.image?.url) &&
          <BannerImage
            src={AppUI.site.heroBanner?.image?.url}
            alt=''
          />
      }
      <TextWrapper hasBanner={isPresent(AppUI.site.heroBanner?.image?.url)}>
        <Title hasBanner={isPresent(AppUI.site.heroBanner?.image?.url)}>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </TextWrapper>
      <CardsWrapper pathCount={learningPaths.length} viewType={viewType}>
        {learningPaths.map(learningPath => (
          <WelcomeScreenCard
            key={learningPath.id}
            title={learningPath.name}
            subtitle={learningPath.subtitle}
            imageUrl={learningPath.imageUrl}
            levelAbbreviation={learningPath.levelAbbreviation}
            levelBadgeColor={learningPath.levelBadgeColor}
            onClick={isPresent(onLearningPathClick) ? () => onLearningPathClick(learningPath.id) : undefined}
            viewType={viewType}
          />
        ))}
      </CardsWrapper>
    </Wrapper>
  );
});

export default WelcomeScreen;